import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { Button, Form, PasswordInput, Stack, InlineNotification } from '@carbon/react';
import { useAuth } from '@/contexts/AuthContext';
import { getClerkErrorMessage } from '@/lib/clerkErrors';
import VerificationCodeInput from '@/components/VerificationCodeInput';
import { AuthPageLayout, ClerkMissingNotice } from '@/components/auth/AuthShell';

const ResetPasswordContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  const { isLoaded, signIn, setActive } = useSignIn();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!email) {
      navigate('/forgot-password', { replace: true });
    }
  }, [email, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isLoaded || !signIn) return;

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (code.length !== 6) {
      setError('Enter the full 6-digit code from your email.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code,
        password,
      });

      if (signIn.status === 'complete') {
        await setActive({ session: signIn.createdSessionId });
        navigate('/dashboard', { replace: true });
        return;
      }

      setError('Password reset could not be completed. Please try again.');
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!isLoaded || !signIn || !email) return;

    setSubmitting(true);
    setError('');

    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
    } catch (err) {
      setError(getClerkErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!email) return null;

  return (
    <AuthPageLayout
      title="Choose a new password"
      description={
        <>
          Enter the 6-digit code sent to <strong>{email}</strong> and your new password.
        </>
      }
      testId="reset-password-page"
    >
      {error && (
        <InlineNotification
          kind="error"
          title="Unable to continue"
          subtitle={error}
          hideCloseButton
          className="auth-form-error"
        />
      )}
      <Form className="auth-form" onSubmit={handleSubmit}>
        <Stack gap={6}>
          <VerificationCodeInput value={code} onChange={setCode} />
          <PasswordInput
            id="reset-password"
            labelText="New password"
            placeholder="Enter a new password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <PasswordInput
            id="reset-password-confirm"
            labelText="Confirm new password"
            placeholder="Re-enter your new password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <Button
            type="submit"
            disabled={!isLoaded || submitting || code.length !== 6 || !password || !confirmPassword}
          >
            {submitting ? 'Updating password…' : 'Update password'}
          </Button>
          <Button kind="tertiary" type="button" disabled={submitting || !isLoaded} onClick={handleResend}>
            Resend code
          </Button>
          <p className="auth-form-switch">
            <Link to="/login" className="auth-form-link">
              Back to sign in
            </Link>
          </p>
        </Stack>
      </Form>
    </AuthPageLayout>
  );
};

const ResetPasswordPage = () => {
  const { clerkMissing } = useAuth();
  if (clerkMissing) return <ClerkMissingNotice />;
  return <ResetPasswordContent />;
};

export default ResetPasswordPage;
